import _ from "lodash";
import styled from "styled-components";

import Koulutusala from "./Koulutusala";
import MuuMaarays from "./MuuMaarays";
import VT from "./VT";

import { KOHTEET } from "./constants";
import Tutkintokieli from "./Tutkintokieli";
import Section from "@/components/03-templates/Section";
import common from "@/i18n/definitions/common";
import { useIntl } from "react-intl";
import { parseLocalizedField } from "@/modules/helpers";
import {
  find,
  compose,
  sortBy,
  prop,
  filter,
  flatten,
  groupBy,
  path,
  prepend,
  values,
  propEq
} from "ramda";
import Typography from "@mui/material/Typography";
import { PropTypes } from "prop-types";
import {
  enimmaisopiskelijamaaraKoodiarvo,
  vahimmaisopiskelijamaaraKoodiarvo
} from "@/helpers/opiskelijavuodet";

const Otsikko = styled.div`
  font-size: 16px;
  margin-bottom: 20px;
`;

const List = styled.p`
  margin-left: 30px;
  margin-bottom: 20px;
`;
const Capitalized = styled.span`
  text-transform: capitalize;
  &:after {
    content: ", ";
  }
  &:last-child:after {
    content: "";
  }
`;
const MuutToimialueet = styled.p`
  margin-bottom: 20px;
`;

const Tietoa = styled.div`
  margin: 20px 0 20px 0;
  white-space: pre-wrap;
`;

const Tutkinnot = styled.div`
  margin: 0 0 10px 0;
`;

const Koulutukset = styled.div``;

const TELMA = styled.div`
  margin: 0 0 20px 0;
`;

const OpiskelijavuosiRajoitukset = styled.div`
  margin-bottom: 20px;
`;

const OpiskelijavuosiCell = styled.div`
  padding-bottom: 5px;
`;

const KohdeKuvaus = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Kuvaus = styled.div`
  margin: 5px 0 7px 0;
`;

const MuutSection = styled.div`
  margin: 0 0 20px 0;
`;

const OpiskelijaVuodet = ({ opiskelijavuodet }) => {
  const intl = useIntl();
  const enimmaisopiskelijamaara = find(
    propEq("koodiarvo", enimmaisopiskelijamaaraKoodiarvo),
    opiskelijavuodet
  );
  const vahimmaisopiskelijamaara = find(
    propEq("koodiarvo", vahimmaisopiskelijamaaraKoodiarvo),
    opiskelijavuodet
  );

  // enimmaismaara korvaa vahimmaismaaran eli silloin jos enimmaismaara on olemassa naytetaan vain enimmaismaara,
  // jos ei ole enimmaismaaraa yritetaan nayttaa vahimmaismaara.
  // jos vahimmaismaaraa eika enimmaismaaraa ole maaritelty, silloin ei nayteta mitaan.
  // missaan tilanteessa ei haluta nayttaa molempia vahimmaismaaraa tai enimmaismaaraa.
  // opiskelijavuodet-taulukko voi sisaltaa molemmat, koska vanhoihin lupiin on ennen muutosta maaritelty vahimmaismaara.
  // lakimuutoksen jalkeen olemasa oleville luville on lisatty/lisataan enimmaismaara. vahimmaismaaraa ei poisteta,
  // vaan se pysyy historiatietona.
  if (enimmaisopiskelijamaara !== undefined) {
    return (
      <OpiskelijavuosiCell
        key={enimmaisopiskelijamaara.koodiarvo + enimmaisopiskelijamaara.arvo}>
        {intl.formatMessage(common.lupaSectionOpiskelijavuodetMaximumMain, {
          arvo: enimmaisopiskelijamaara.arvo // opiskelijavuosien pitää näkyä vaikka arvo olisi nolla.
        })}
      </OpiskelijavuosiCell>
    );
  } else if (vahimmaisopiskelijamaara !== undefined) {
    return (
      <OpiskelijavuosiCell
        key={
          vahimmaisopiskelijamaara.koodiarvo + vahimmaisopiskelijamaara.arvo
        }>
        {intl.formatMessage(common.lupaSectionOpiskelijavuodetMinimum, {
          arvo: vahimmaisopiskelijamaara.arvo
        })}
      </OpiskelijavuosiCell>
    );
  } else {
    return null; // Skip rendering
  }
};

const OpiskelijaVuodetRajoitukset = ({ rajoitukset, kohdeKuvaus, ytunnus }) => {
  const intl = useIntl();
  // Suodatetaan rajoitukset mukaan joiden arvo on suurempi kuin nolla.
  let filteredRajoitukset = rajoitukset.filter(e => parseInt(e.arvo, 10) > 0);
  // Koodiarvoja käytetään erikoistapausta varten. Käyttöliittymässä näytetään tietty teksti, jos kohteena on jokin vaativan erityisen tuen tehtävä.
  // Koodiarvot ja koodisto tulee opintoppolusta, ja niiden muuttaminen rikkoo toteutuksen. Tästä syystä erityisen tehtävän koodiarvoja ei kannata muokata opintipolussa lainkaan.
  // Tutkinnot, TUVA ja TELMA vaativan erityisen tuen opiskelijoille koodiarvo 2, koodisto oivamuutoikeudetvelvollisuudetehdotjatehtavat näkyy käyttöliittymässä seuraavasti:
  // Näytetään teksti: Vaativan erityisen tuen tehtävään käytettävien opiskelijavuosien enimmäismäärä on 88
  // Jos koodiarvo muutetaan joksikin toiseksi kuin alla olevassa listassa, näytetään teksti: Tutkinnot, TUVA ja TELMA vaativan erityisen tuen opiskelijoille enintään 88 opiskelijavuotta
  // Opintopolun koodiarvojen muutokset eivät suoraan riko mitään, mutta ne muuttavat mitä käyttöliittymässä näkyy.
  const erityinenTehtavaKoodiarvot = [
    "2",
    "16",
    "17",
    "18",
    "19",
    "20",
    "21",
    "23"
  ];
  const erityinenTehtavaKoodisto =
    "oivamuutoikeudetvelvollisuudetehdotjatehtavat";
  return filteredRajoitukset.length > 0 ? (
    <div>
      <KohdeKuvaus>
        {ytunnus === "0244767-4"
          ? intl.formatMessage(common.lupaSectionOpiskelijavuodetValtio)
          : kohdeKuvaus}
      </KohdeKuvaus>
      <OpiskelijavuosiRajoitukset>
        {_.map(filteredRajoitukset, (obj, i) => {
          const { tyyppi, arvo } = obj;
          if (arvo <= 0) {
            // opiskelijavuosi rajoituksia ei haluta näyttää, jos arvo on 0 tai pienempi.
            return null;
          }
          return (
            <div>
              <span className={"data cell"}>
                {obj.koodisto === erityinenTehtavaKoodisto &&
                erityinenTehtavaKoodiarvot.includes(obj.koodiarvo) ? (
                  <>
                    {intl.formatMessage(
                      common.lupaSectionOpiskelijavuodetMaximumErityinen,
                      {
                        tyyppi,
                        arvo
                      }
                    )}
                  </>
                ) : (
                  <>
                    {parseLocalizedField(
                      obj.koodi.metadata,
                      intl.locale.toUpperCase(),
                      "nimi"
                    )}
                    {intl.formatMessage(
                      common.lupaSectionOpiskelijavuodetMaximum,
                      {
                        tyyppi,
                        arvo
                      }
                    )}
                  </>
                )}
              </span>
            </div>
          );
        })}
      </OpiskelijavuosiRajoitukset>
    </div>
  ) : null;
};

const LupaSection = props => {
  const { kohde, ytunnus, lupaAlkuPvm, kielet } = props;
  const intl = useIntl();

  if (kohde) {
    const { tunniste, heading, headingNumber } = kohde;

    const section = list =>
      list.length > 0 ? (
        <MuutSection key={Math.random()}>
          <Typography component="h4" variant="h4" className="text-bold">
            {list[0].tyyppi}
          </Typography>
          {_.map(list, (item, i) => {
            const { kuvaus, value } = item;
            return (
              <div key={i}>
                <Kuvaus>{kuvaus}</Kuvaus>
                {value && <Kuvaus>{value}</Kuvaus>}
              </div>
            );
          })}
        </MuutSection>
      ) : null;

    // Logiikka eri kohteille
    switch (tunniste) {
      // Kohde 1: Tutkinnot
      case KOHTEET.TUTKINNOT: {
        const {
          maaraykset,
          muutMaaraykset,
          lisatietojaTutkinnot,
          lisatietojaKoulutus
        } = kohde;

        // TELMA
        const telma = muutMaaraykset.filter(item => item.koodi === "999903");

        return (
          <div className="border-b border-b-gray">
            <Section code={headingNumber} title={heading} titleElement="h3">
              {maaraykset && maaraykset.length > 0 && (
                <Otsikko>
                  {intl.formatMessage(common.lupaSectionTitleAllLupas)}
                </Otsikko>
              )}
              <Tutkinnot>
                {_.map(maaraykset, (ala, i) => (
                  <Koulutusala key={i} {...ala} lupaAlkuPvm={lupaAlkuPvm} />
                ))}
              </Tutkinnot>
              {maaraykset &&
              maaraykset.length > 0 &&
              lisatietojaTutkinnot.length === 0 ? (
                <Tietoa>
                  {intl.formatMessage(common.lupaSectionTitleSupplementary)}
                </Tietoa>
              ) : (
                lisatietojaTutkinnot.map((lisatieto, i) => {
                  return <Tietoa key={i}>{lisatieto.meta.arvo}</Tietoa>;
                })
              )}

              <Koulutukset>
                {telma && telma[0] && (
                  <TELMA>
                    {/* Selite vain yhden kerran */}
                    <p>{telma[0].selite}</p>
                    {telma.map(({ koodi, nimi }, i) => (
                      <VT key={`telma${i}`} koodi={koodi} nimi={nimi} />
                    ))}
                  </TELMA>
                )}
                {_.map(
                  muutMaaraykset.filter(
                    item =>
                      item.koodisto === "koulutus" ||
                      item.koodisto ===
                        "ammatilliseentehtavaanvalmistavakoulutus"
                  ),
                  (items, i) => (
                    <MuuMaarays key={i} {...items} />
                  )
                )}
                {_.map(
                  muutMaaraykset.filter(
                    item => item.koodisto === "kuljettajakoulutus"
                  ),
                  (items, i) => (
                    <MuuMaarays key={"k" + i} {...items} />
                  )
                )}
                {_.map(
                  muutMaaraykset.filter(
                    item => item.koodisto === "oivatyovoimakoulutus"
                  ),
                  (items, i) => (
                    <MuuMaarays key={"t" + i} {...items} />
                  )
                )}
              </Koulutukset>
              {lisatietojaKoulutus
                ? lisatietojaKoulutus.map((lisatieto, i) => {
                    return <Tietoa key={i}>{lisatieto.meta.arvo}</Tietoa>;
                  })
                : null}
            </Section>
          </div>
        );
      }

      // Kohde 2: Opetuskieli
      case KOHTEET.KIELI: {
        const {
          kohdeKuvaus,
          kohdeArvot,
          tutkintokieletArr,
          lisatietoja,
          tutkintoOsaamisalaObj
        } = kohde;

        tutkintokieletArr.forEach(tutkintokieli => {
          tutkintokieli.kieli = parseLocalizedField(
            values(
              (find(k => k.koodiarvo === tutkintokieli.kieli, kielet) || {})
                .metadata
            ),
            intl.locale.toUpperCase()
          );
        });
        const tutkintokieletArrSorted = _.sortBy(tutkintokieletArr, "kieli");

        return (
          <div className="border-b border-b-gray">
            <Section code={headingNumber} title={heading} titleElement="h3">
              <p>{kohdeKuvaus}</p>
              <List>
                {_.map(kohdeArvot, (arvo, i) => (
                  <Capitalized key={i}>{_.capitalize(arvo.label)}</Capitalized>
                ))}
              </List>
              <Tutkinnot>
                {tutkintokieletArrSorted.map((tutkintokieli, i) => {
                  return (
                    <div key={i}>
                      {intl.formatMessage(
                        common.lupaSectionTutkintokieliAdditionalLanguages
                      )}
                      &nbsp;
                      {tutkintokieli.kieli}&nbsp;
                      {tutkintokieli.tutkinnot.length <= 1
                        ? intl.formatMessage(
                            common.lupaSectionTutkintokieliFollowingDegreesSingular
                          )
                        : intl.formatMessage(
                            common.lupaSectionTutkintokieliFollowingDegreesPlural
                          )}
                      {tutkintokieli.tutkinnot.map((tutkinto, i) => {
                        const osaamisalaName = parseLocalizedField(
                          tutkintoOsaamisalaObj[tutkinto.tutkintokoodi]?.[0]
                            ?.koodi?.metadata,
                          intl.locale.toUpperCase(),
                          "nimi"
                        );
                        const osaamisalaKoodiarvo =
                          tutkintoOsaamisalaObj?.[tutkinto.tutkintokoodi]?.[0]
                            ?.koodiarvo || "";

                        const osaamisalaRajoiteName =
                          osaamisalaName && osaamisalaKoodiarvo
                            ? osaamisalaKoodiarvo + " " + osaamisalaName
                            : "";
                        return (
                          <Tutkintokieli
                            key={i}
                            {...tutkinto}
                            osaamisalaRajoiteName={osaamisalaRajoiteName}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </Tutkinnot>
              {lisatietoja
                ? lisatietoja.map((lisatieto, i) => {
                    return <Tietoa key={i}>{lisatieto.meta.arvo}</Tietoa>;
                  })
                : null}
            </Section>
          </div>
        );
      }

      // Kohde 3: Toiminta-alueet
      case KOHTEET.TOIMIALUE: {
        const { kohdeKuvaus, maakunnat, kunnat, lisatietoja } = kohde;

        return (
          <div className="border-b border-b-gray">
            <Section code={headingNumber} title={heading} titleElement="h3">
              <p>{kohdeKuvaus}</p>
              <List>
                {_.map(maakunnat, (maakunta, i) => (
                  <Capitalized key={i}>{maakunta.arvo}</Capitalized>
                ))}
              </List>
              <List>
                {_.map(kunnat, (kunta, i) => (
                  <Capitalized key={i}>{kunta.arvo}</Capitalized>
                ))}
              </List>
              {maakunnat.length > 0 || kunnat.length > 0 ? (
                <MuutToimialueet>
                  {intl.formatMessage(common.lupaSectionToimintaAlueNational)}
                </MuutToimialueet>
              ) : null}
              {lisatietoja
                ? lisatietoja.map((lisatieto, i) => {
                    return <Tietoa key={i}>{lisatieto.meta.arvo}</Tietoa>;
                  })
                : null}
            </Section>
          </div>
        );
      }

      // Kohde 4: Opiskelijavuodet
      //  OpiskelijaVuodet = oppiskelijavuosien enimmäismäärä korvaa vähimmäismäärän. Vähimmäismäärä näytetään jos enimmäismäärä on tyhjä.
      //  OpiskelijaVuodetRajoitukset = opiskelijavuosiin kohdennettu enimmäismäärä rajoite:
      //    - esim. Opiskelijavuosien enimmäismäärää koskee seuraava rajoitus: Sisäoppilaitosmuotoinen koulutus enintään x opiskelijavuotta
      //    - vanhoissa käännöksissä teksti voi olla muodossa opiskelijavuosien vähimmäismäärää koskee.. muodossa.
      //  Opiskelijavuosien enimmäismäärä korvaa vähimmäismäärän ja vuoden 2025 jälkeeen vähimmäismäärää ei käytetä.
      //    - Vuoden 2025 jälkeen voidaan poistaa tästä tiedostosta ja muualta kaikki viittaukset vähimmäismäärään.
      case KOHTEET.OPISKELIJAVUODET: {
        const { opiskelijavuodet, rajoitukset, kohdeKuvaus, lisatietoja } =
          kohde;
        return (
          <div className="border-b border-b-gray">
            <Section code={headingNumber} title={heading} titleElement="h3">
              <OpiskelijaVuodet opiskelijavuodet={opiskelijavuodet} />
              <OpiskelijaVuodetRajoitukset
                rajoitukset={rajoitukset}
                kohdeKuvaus={kohdeKuvaus}
                ytunnus={ytunnus}
              />
              {lisatietoja
                ? lisatietoja.map((lisatieto, i) => {
                    return <Tietoa key={i}>{lisatieto.meta.arvo}</Tietoa>;
                  })
                : null}
            </Section>
          </div>
        );
      }

      // Kohde 5: Muut määräykset
      case KOHTEET.MUUT: {
        const { muutCombined, ...rest } = kohde;

        const muut = groupBy(
          prop("tyyppi"),
          sortBy(
            compose(parseInt, prop("koodiarvo")),
            flatten(Object.keys(filter(Array.isArray, rest)).map(x => rest[x]))
          )
        );

        return (
          <div>
            <Section code={headingNumber} title={heading} titleElement="h3">
              {_.map(muut, muu => {
                const createdAtPath = ["meta", "createdAt"];
                const _muu = path(prepend(0, createdAtPath), muu)
                  ? sortBy(path(createdAtPath), muu)
                  : muu;
                return section(_muu);
              })}

              {muutCombined.length === 0 ? (
                <p>{intl.formatMessage(common.lupaSectionMuutEiMaarayksia)}</p>
              ) : null}
            </Section>
          </div>
        );
      }

      default: {
        return (
          <div className="border-b border-b-gray">
            <Section code={headingNumber} title={heading} titleElement="h3" />
          </div>
        );
      }
    }
  } else {
    return (
      <div className="border-b border-b-gray">
        <Typography component="h3" variant="h3">
          {intl.formatMessage(common.lupaSectionNoInfo)}
        </Typography>
      </div>
    );
  }
};

LupaSection.propTypes = {
  kohde: PropTypes.object,
  ytunnus: PropTypes.string,
  lupaAlkuPvm: PropTypes.string,
  kielet: PropTypes.array
};

OpiskelijaVuodet.propTypes = {
  opiskelijavuodet: PropTypes.array
};

export default LupaSection;
